import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { store, defaultData } from "@/lib/storage";
import { api } from "@/lib/accessApi";

const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [entitled, setEntitled] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState("");
  const [data, setDataState] = useState(defaultData());
  const [ready, setReady] = useState(false);

  const applyStatus = useCallback((status) => {
    setCheckoutUrl(status.checkout_url || "");
    if (status.authenticated) {
      const u = { id: status.email, email: status.email, name: status.name || "" };
      setUser(u);
      setEntitled(!!status.entitled);
      const d = store.getData(u.id);
      if (status.name && !d.profile.name) d.profile = { ...d.profile, name: status.name };
      setDataState(d);
      return { user: u, entitled: !!status.entitled };
    }
    setUser(null);
    setEntitled(false);
    setDataState(defaultData());
    return { user: null, entitled: false };
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const status = await api("/access/status");
        if (active) applyStatus(status);
      } catch {
        /* offline / not authenticated */
      }
      if (active) setReady(true);
    })();
    return () => { active = false; };
  }, [applyStatus]);

  const updateData = useCallback((patch) => {
    setDataState((prev) => {
      const next = typeof patch === "function" ? patch(prev) : { ...prev, ...patch };
      if (user) store.setData(user.id, next);
      return next;
    });
  }, [user]);

  const updateProfile = useCallback((patch) => {
    updateData((prev) => ({ ...prev, profile: { ...prev.profile, ...patch } }));
  }, [updateData]);

  const register = useCallback(async ({ name, email, password }) => {
    try {
      await api("/access/register", { method: "POST", body: { name, email, password } });
      const status = await api("/access/status");
      return applyStatus(status);
    } catch (err) {
      return { error: typeof err.detail === "string" ? err.detail : "Pendaftaran gagal." };
    }
  }, [applyStatus]);

  const login = useCallback(async ({ email, password }) => {
    try {
      await api("/access/login", { method: "POST", body: { email, password } });
      const status = await api("/access/status");
      return applyStatus(status);
    } catch (err) {
      return { error: typeof err.detail === "string" ? err.detail : "Email atau kata sandi salah." };
    }
  }, [applyStatus]);

  const activate = useCallback(async ({ email, code }) => {
    try {
      await api("/access/activate", { method: "POST", body: { email, code } });
      const status = await api("/access/status");
      return applyStatus(status);
    } catch (err) {
      return { error: typeof err.detail === "string" ? err.detail : "Email atau kode akses tidak valid." };
    }
  }, [applyStatus]);

  const logout = useCallback(async () => {
    try { await api("/access/logout", { method: "POST" }); } catch { /* ignore */ }
    setUser(null);
    setEntitled(false);
    setDataState(defaultData());
  }, []);

  const displayName = user?.name || data?.profile?.name || "";

  const value = {
    user, entitled, checkoutUrl, data, ready, displayName,
    register, login, activate, logout,
    updateData, updateProfile,
  };
  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}
